export type ListingPortal =
  | 'idealista'
  | 'fotocasa'
  | 'properstar'
  | 'habitaclia'
  | 'homegate'
  | 'immoscout24'
  | 'newhome'
  | 'acheterlouer'
  | 'urbanhome'
  | 'immobilierch'
  | 'icasa'
  | 'dreamo'
  | 'flatfox'
  | 'leboncoin'
  | 'seloger'
  | 'bienici'
  | 'pap'
  | 'immobiliare';

export type ListingPortalCountry = 'ES' | 'CH' | 'FR' | 'IT';

export type ParsedListingUrl = {
  portal: ListingPortal;
  country: ListingPortalCountry;
  originalId: string;
};

export type ParseListingUrlError =
  | { kind: 'invalid_url' }
  | { kind: 'unsupported_portal'; host: string }
  | { kind: 'wrong_country'; portalCountry: ListingPortalCountry }
  | { kind: 'no_id_found'; portal: ListingPortal };

export type ParseListingUrlResult =
  | { ok: true; value: ParsedListingUrl }
  | { ok: false; error: ParseListingUrlError };

type PortalConfig = {
  portal: ListingPortal;
  country: ListingPortalCountry;
  matchesHost: (host: string) => boolean;
  extractOriginalId: (pathname: string) => string | null;
};

const matchAt = (pathname: string, pattern: RegExp): string | null =>
  pathname.match(pattern)?.[1] ?? null;

const PORTAL_CONFIGS: PortalConfig[] = [
  {
    portal: 'idealista',
    country: 'ES',
    matchesHost: host => /(^|\.)idealista\.com$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/inmueble\/(\d+)\/?/),
  },
  {
    portal: 'fotocasa',
    country: 'ES',
    matchesHost: host => /(^|\.)fotocasa\.es$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/(\d+)\/d\/?$/),
  },
  {
    portal: 'properstar',
    country: 'ES',
    matchesHost: host => /(^|\.)properstar\.es$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/listing\/(\d+)/),
  },
  {
    portal: 'habitaclia',
    country: 'ES',
    matchesHost: host => /(^|\.)habitaclia\.com$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /-i(\d+)\.htm$/),
  },

  {
    portal: 'homegate',
    country: 'CH',
    matchesHost: host => /(^|\.)homegate\.ch$/.test(host),
    extractOriginalId: pathname =>
      matchAt(pathname, /\/(?:buy|rent)\/(\d+)\/?$/),
  },
  {
    portal: 'immoscout24',
    country: 'CH',
    matchesHost: host => /(^|\.)immoscout24\.ch$/.test(host),
    extractOriginalId: pathname =>
      matchAt(pathname, /\/(?:buy|rent)\/(\d+)\/?$/),
  },
  {
    portal: 'newhome',
    country: 'CH',
    matchesHost: host => /(^|\.)newhome\.ch$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/detail\/(\d+)\/?$/),
  },
  {
    portal: 'acheterlouer',
    country: 'CH',
    matchesHost: host => /(^|\.)acheter-louer\.ch$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /-(\d+)\.html?$/),
  },
  {
    portal: 'urbanhome',
    country: 'CH',
    matchesHost: host => /(^|\.)urbanhome\.ch$/.test(host),
    // /suchen/ (de) or /recherche/ (fr) — both locale paths are used.
    extractOriginalId: pathname =>
      matchAt(pathname, /\/(?:suchen|recherche)\/(\d+)/),
  },
  {
    portal: 'immobilierch',
    country: 'CH',
    matchesHost: host => /(^|\.)immobilier\.ch$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /-(\d+)\/?$/),
  },
  {
    portal: 'icasa',
    country: 'CH',
    matchesHost: host => /(^|\.)icasa\.ch$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /-(\d+)\.html?$/),
  },
  {
    portal: 'dreamo',
    country: 'CH',
    matchesHost: host => /(^|\.)dreamo\.ch$/.test(host),
    // original_id is the full /o/<token> slug; capture up to the next / or ?.
    extractOriginalId: pathname => matchAt(pathname, /\/o\/([^/?]+)/),
  },
  {
    portal: 'flatfox',
    country: 'CH',
    matchesHost: host => /(^|\.)flatfox\.ch$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/(\d+)\/?$/),
  },

  {
    portal: 'leboncoin',
    country: 'FR',
    matchesHost: host => /(^|\.)leboncoin\.fr$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/(\d+)\/?$/),
  },
  {
    portal: 'seloger',
    country: 'FR',
    matchesHost: host => /(^|\.)seloger\.com$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/(\d+)\.htm$/),
  },
  {
    portal: 'bienici',
    country: 'FR',
    matchesHost: host => /(^|\.)bienici\.com$/.test(host),
    // bienici original_id is alphanumeric (e.g. "safti-1-1646169")
    extractOriginalId: pathname => matchAt(pathname, /\/annonce\/([^/]+?)\/?$/),
  },
  {
    portal: 'pap',
    country: 'FR',
    matchesHost: host => /(^|\.)pap\.fr$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /-r(\d+)\/?$/),
  },
  {
    portal: 'properstar',
    country: 'FR',
    matchesHost: host => /(^|\.)properstar\.fr$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/listing\/(\d+)/),
  },

  {
    portal: 'idealista',
    country: 'IT',
    matchesHost: host => /(^|\.)idealista\.it$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/immobile\/(\d+)\/?/),
  },
  {
    portal: 'immobiliare',
    country: 'IT',
    matchesHost: host => /(^|\.)immobiliare\.it$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/annunci\/(\d+)\/?/),
  },

  // properstar CH last so the .fr/.es host variants match first.
  {
    portal: 'properstar',
    country: 'CH',
    matchesHost: host => /(^|\.)properstar\.ch$/.test(host),
    extractOriginalId: pathname => matchAt(pathname, /\/listing\/(\d+)/),
  },
];

export const parseListingUrl = (
  rawUrl: string,
  tenantCountry: string,
): ParseListingUrlResult => {
  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return { ok: false, error: { kind: 'invalid_url' } };
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { ok: false, error: { kind: 'invalid_url' } };
  }

  const host = url.hostname;
  const config = PORTAL_CONFIGS.find(c => c.matchesHost(host));
  if (!config) {
    return { ok: false, error: { kind: 'unsupported_portal', host } };
  }

  if (config.country !== tenantCountry) {
    return {
      ok: false,
      error: { kind: 'wrong_country', portalCountry: config.country },
    };
  }

  const originalId = config.extractOriginalId(url.pathname);
  if (originalId == null) {
    return { ok: false, error: { kind: 'no_id_found', portal: config.portal } };
  }

  return {
    ok: true,
    value: {
      portal: config.portal,
      country: config.country,
      originalId,
    },
  };
};
